import React, {useEffect, useState} from 'react';
import {Alert, Descriptions, Input, message, Modal, Select, Space, Spin} from 'antd';
import {useLazyQuery, useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  RESOLVE_COMPLAINT_ORG_STAFF,
  ResolveComplaintOrgStaffResponse,
} from '../../../../graphql/complaintOrg/queries';
import {
  ADD_COMPLAINT_ORG_MEMBER,
  AddComplaintOrgMemberResponse,
  AddComplaintOrgMemberVariables,
} from '../../../../graphql/complaintOrg/mutations';
import {ComplaintOrgRoleCode} from '../../../../graphql/complaintOrg/types';
import {translateGraphQLError} from '../../../../utils/graphqlUtils';

/** 添加到哪个位置：角色 + 作用域（系统管理员无 hub / 仓） */
export interface AddMemberTarget {
  roleCode: ComplaintOrgRoleCode;
  hubId?: number;
  warehouseId?: number;
  /** 弹窗副标题，如 "MIA HUB" / "MIA HUB · WPB" */
  scopeLabel?: string;
}

interface AddMemberModalProps {
  target: AddMemberTarget | null;
  onClose: () => void;
  onAdded: () => void;
}

/** 与后端校验一致：+1 为 10 位、+86 为 11 位 */
const PHONE_LENGTH: Record<number, number> = {1: 10, 86: 11};

const normalizePhone = (raw: string) => raw.replace(/[\s-]/g, '');

const isValidPhone = (countryCode: number, phone: string) =>
  /^\d+$/.test(phone) && phone.length === PHONE_LENGTH[countryCode];

/**
 * 添加人员（原型 image01）：输入手机号 → 自动带出系统账号 / Lark 账号 / 上级手机号 / 上级 Lark 账号，
 * 只有解析结果为 OK 才能确认；保存时后端会再校验一次。
 */
const AddMemberModal: React.FC<AddMemberModalProps> = ({target, onClose, onAdded}) => {
  const {t} = useTranslation();
  const [countryCode, setCountryCode] = useState<number>(1);
  const [phoneInput, setPhoneInput] = useState('');

  const [resolve, {data, loading: resolving, error: resolveError, variables: resolvedFor}] =
    useLazyQuery<ResolveComplaintOrgStaffResponse>(RESOLVE_COMPLAINT_ORG_STAFF, {fetchPolicy: 'network-only'});
  const [addMember, {loading: adding}] =
    useMutation<AddComplaintOrgMemberResponse, AddComplaintOrgMemberVariables>(ADD_COMPLAINT_ORG_MEMBER);

  const phone = normalizePhone(phoneInput);
  const phoneValid = isValidPhone(countryCode, phone);

  // 每次打开重置
  useEffect(() => {
    if (target) {
      setCountryCode(1);
      setPhoneInput('');
    }
  }, [target]);

  // 号码位数够了自动解析
  useEffect(() => {
    if (target && phoneValid) {
      resolve({variables: {countryCode, phone}});
    }
  }, [target, countryCode, phone, phoneValid, resolve]);

  // 只认当前号码的解析结果，避免改号后沿用上一个号码的结论
  const isCurrent = resolvedFor?.countryCode === countryCode && resolvedFor?.phone === phone;
  const resolved = phoneValid && isCurrent && !resolveError ? data?.resolveComplaintOrgStaff : undefined;
  const canSubmit = !!resolved && resolved.resolveStatus === 'OK' && !resolving;

  const handleOk = async () => {
    if (!target || !canSubmit) return;
    try {
      await addMember({
        variables: {
          input: {
            roleCode: target.roleCode,
            hubId: target.hubId ?? null,
            warehouseId: target.warehouseId ?? null,
            countryCode,
            phone,
          },
        },
      });
      message.success(t('complaintOrg.addSuccess'));
      onAdded();
      onClose();
    } catch (e) {
      message.error(translateGraphQLError(e, 'complaintOrg.errors.addFailed'));
    }
  };

  // 系统账号栏只展示系统账号（产品确认，不展示业务角色）
  const systemAccountText = resolved?.systemAccount?.loginName || '—';

  return (
    <Modal
      open={!!target}
      title={
        <div>
          <div>{target ? t('complaintOrg.modal.title', {role: t(`complaintOrg.role.${target.roleCode}`)}) : ''}</div>
          {target?.scopeLabel && <div className="text-xs font-normal text-gray-400">{target.scopeLabel}</div>}
        </div>
      }
      okText={t('common.confirm')}
      cancelText={t('common.cancel')}
      okButtonProps={{disabled: !canSubmit, loading: adding}}
      onOk={handleOk}
      onCancel={onClose}
      destroyOnClose
      width={440}
    >
      <Space.Compact className="w-full mb-4">
        <Select
          value={countryCode}
          onChange={setCountryCode}
          style={{width: 90}}
          options={[{value: 1, label: '+1'}, {value: 86, label: '+86'}]}
        />
        <Input
          value={phoneInput}
          onChange={(e) => setPhoneInput(e.target.value)}
          placeholder={t('complaintOrg.modal.phonePlaceholder') ?? ''}
          allowClear
        />
      </Space.Compact>

      {phone.length > 0 && !phoneValid && (
        <Alert type="warning" showIcon className="mb-3" message={t('complaintOrg.modal.invalidPhone')}/>
      )}

      <Spin spinning={resolving}>
        <Descriptions bordered column={1} size="small" labelStyle={{width: 140}}>
          <Descriptions.Item label={t('complaintOrg.modal.systemAccount')}>{systemAccountText}</Descriptions.Item>
          <Descriptions.Item label={t('complaintOrg.modal.larkName')}>{resolved?.larkName || '—'}</Descriptions.Item>
          <Descriptions.Item label={t('complaintOrg.modal.superiorPhone')}>{resolved?.superiorPhone || '—'}</Descriptions.Item>
          <Descriptions.Item label={t('complaintOrg.modal.superiorLarkName')}>
            {resolved?.superiorLarkName || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('complaintOrg.modal.superiorSystemAccount')}>
            {resolved?.superiorSystemAccount?.loginName || '—'}
            {resolved?.superiorSystemAccount && resolved.superiorSystemAccount.accountStatus !== 'NORMAL' && (
              <span className="ml-2 text-orange-500">
                {t(`complaintOrg.status.${resolved.superiorSystemAccount.accountStatus}`)}
              </span>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Spin>

      {phoneValid && isCurrent && resolveError && (
        <Alert
          type="error"
          showIcon
          className="mt-3"
          message={translateGraphQLError(resolveError, 'complaintOrg.errors.resolveFailed')}
        />
      )}
      {resolved && resolved.resolveStatus !== 'OK' && (
        <Alert
          type="error"
          showIcon
          className="mt-3"
          message={t(`complaintOrg.modal.resolveStatus.${resolved.resolveStatus}`)}
        />
      )}
    </Modal>
  );
};

export default AddMemberModal;
